import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/supabase-context';

// Generate a session ID that persists for the browser session
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('page_tracking_session_id');
  if (!sessionId) {
    sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substring(2, 15);
    sessionStorage.setItem('page_tracking_session_id', sessionId);
  }
  return sessionId;
};

// Debounce function to prevent excessive tracking
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const usePageTracking = () => {
  const location = useLocation();
  const { supabase, user } = useAuth();
  const lastTrackedPath = useRef(null);
  const trackingEnabled = useRef(true);

  // Create a debounced tracking function
  const trackPageView = useRef(
    debounce(async (path, referrer) => {
      // Skip tracking if disabled or if it's the same path as last tracked
      if (!trackingEnabled.current || lastTrackedPath.current === path) {
        return;
      }

      try {
        // Get session ID
        const sessionId = getSessionId();
        
        // Prepare tracking data
        const trackingData = {
          path: path,
          referrer: referrer || document.referrer || null,
          user_agent: navigator.userAgent || null,
          user_id: user?.id || null,
          session_id: sessionId
        };

        // Track the page view asynchronously without blocking the UI
        supabase
          .from('page_views')
          .insert([trackingData])
          .then(({ error }) => {
            if (error) {
              console.warn('Page tracking error:', error);
            }
          });

        // Update last tracked path
        lastTrackedPath.current = path;
      } catch (error) {
        console.warn('Page tracking error:', error);
      }
    }, 500) // 500ms debounce
  ).current;

  useEffect(() => {
    // Only track if supabase is available
    if (!supabase) return;

    // Get current path
    const currentPath = location.pathname + location.search;
    
    // Track the page view
    trackPageView(currentPath, lastTrackedPath.current);
  }, [location, supabase, user, trackPageView]);

  // Function to disable tracking (useful for admin pages or sensitive areas)
  const disableTracking = () => {
    trackingEnabled.current = false;
  };

  // Function to enable tracking
  const enableTracking = () => {
    trackingEnabled.current = true;
  };

  return {
    disableTracking,
    enableTracking,
    isTrackingEnabled: trackingEnabled.current
  };
};

export default usePageTracking;
